import { useQuery } from '@apollo/client';
import { Box, CircularProgress } from '@mui/material';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Count } from '~/components/count';
import { DataGridPagination } from '~/components/data-grid';
import { SearchBar } from '~/components/forms/search-bar';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams } from '~/hooks/pagination';
import { AssetLink } from '../components/AssetLink';
import { ReportDataGrid } from '../components/ReportDataGrid';
import {
  ListTabHeader,
  LoadingSpinner,
  useDeviceMetricColumns,
  useEventMetricColumns,
} from '../lib';
import type { PlayoutReport__SponsorSummary as Row } from '../queries/PlayoutReportBySponsor.generated';
import { PlayoutReportBySponsorDocument } from '../queries/PlayoutReportBySponsor.generated';
import { usePlayoutReport } from './context';

export const PlayoutReportsBySponsor = () => {
  const { t } = useTranslation();
  const { variables, querySkipped } = usePlayoutReport();
  const navigate = useNavigate();
  const { sortModel, setOrderByParams } = useOrderByParams();
  const { paginationModel, setPageParams } = usePaginationParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { data, loading } = useQuery(PlayoutReportBySponsorDocument, {
    skip: querySkipped,
    fetchPolicy: 'cache-and-network',
    variables,
  });

  const filteredRows = useMemo(() => {
    const rows = data?.network?.playoutReport.bySponsorSummary ?? [];
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => row.sponsor.toLowerCase().includes(query));
  }, [data, search]);

  const eventMetricColumns = useEventMetricColumns<Row>();
  const deviceColumns = useDeviceMetricColumns<Row>();

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'sponsor',
        headerName: t('pages.playout_reports.top_assets.columns.sponsor'),
        flex: 1.5,
        minWidth: 180,
        hideable: false,
        renderCell: ({ row }) => (
          <AssetLink
            kind="sponsorApp"
            mediaItemId={null}
            contentItemId={row.sponsorId}
            contentFolderId={row.contentFolderId ?? null}
            label={row.sponsor}
          />
        ),
      },
      ...eventMetricColumns,
      ...deviceColumns,
    ],
    [t, eventMetricColumns, deviceColumns],
  );

  return (
    <Box>
      <ListTabHeader>
        <Box sx={{ width: 'min(280px, 100%)' }}>
          <SearchBar
            placeholder={t('pages.playout_reports.by_sponsor.search_placeholder')}
            search={search}
            onChange={(value) =>
              setSearchParams((params) => {
                params.set('search', value);
                params.set('page', '1');
                return params;
              })
            }
          />
        </Box>
        <Count
          selectedCount={0}
          totalCount={filteredRows.length}
          thing="common.pluralize.sponsor_plural"
        />
      </ListTabHeader>
      {loading && !data ? (
        <LoadingSpinner>
          <CircularProgress aria-label={t('common.status.loading')} size={60} thickness={4} />
        </LoadingSpinner>
      ) : (
        <ReportDataGrid
          columnStateKey="bySponsor"
          columns={columns}
          rows={filteredRows}
          disableRowSelectionOnClick
          getRowId={(r: Row) => r.sponsorId}
          onRowClick={({ row }: GridRowParams<Row>) =>
            navigate({ pathname: `${row.sponsorId}`, search: searchParams.toString() })
          }
          pageSizeOptions={[25, 50, 100]}
          sortModel={sortModel}
          onSortModelChange={(m) => setOrderByParams(m.at(0) ?? null)}
          paginationModel={paginationModel}
          onPaginationModelChange={(m) => setPageParams({ page: m.page + 1, perPage: m.pageSize })}
          slots={{ pagination: DataGridPagination }}
          localeText={{ noRowsLabel: t('pages.playout_reports.by_sponsor.empty') }}
        />
      )}
    </Box>
  );
};
